import type { Locale } from '@sentra/contracts';
import { v4 as uuidv4 } from 'uuid';

import { ApiError, apiErrorFrom, ResponseShapeError, TransportError } from './errors';
import type { AnyRoute, BinaryRouteSpec, BodyOf, PathParams, QueryOf, ResponseOf } from './routes';
import { UNSAFE_METHODS } from './routes';
import type { SseFrame } from './sse';
import { createSseParser } from './sse';

/**
 * The one way this application talks to its API.
 *
 * **Every request is same-origin and relative.** Section 1.1 serves the SPA from the same origin as
 * the API, so there is no base URL to configure and no cross-origin mode to get wrong;
 * `credentials: 'same-origin'` is what carries the session cookie, and the CSRF token is echoed in
 * a header because the cookie holding it is `httpOnly` and script cannot read it.
 *
 * **`fetch` is injected, never captured.** The unit and web tiers replace the global with a guard
 * that refuses egress and names the host it caught, so a client that closed over `globalThis.fetch`
 * would be untestable in one direction and would silently hit the guard in the other. Passing it in
 * also means a test asserts on the exact request that was built rather than on a mock of a mock.
 *
 * **Every answer is parsed against the route's contract schema.** A 2xx whose body is not the
 * documented shape becomes a {@link ResponseShapeError} rather than data: the alternative is a
 * screen rendering `undefined` where a person's name belongs, and in this product the difference
 * between "no record" and "a record this client could not read" is the difference between a
 * duplicate registration and a question.
 *
 * **Every unsafe request carries an idempotency key.** AD-30 stops a duplicate submission with a
 * key rather than a time window, and the key is minted by the CALLER, once per logical submission,
 * and handed here — so a retry of the same submission carries the same key and is answered with the
 * original response, while two deliberate submissions carry two keys and are two writes. A client
 * that minted one per HTTP attempt would provide no protection at all, which is why
 * {@link RequestOptions.idempotencyKey} is an argument rather than something this module draws.
 */

/** A key, in the shape the server's own validator demands: a uuid and nothing else. */
export function newIdempotencyKey(): string {
  return uuidv4();
}

/**
 * The query values a route may be given, once the route's own schema has already decided the KEY
 * SET. `undefined` means "do not send this parameter".
 *
 * This is the serialisation type — what {@link appendQuery} knows how to put in a URL — and not the
 * type a caller is checked against. That is {@link QueryOf}, which names the members the route
 * actually declares, so a parameter the server would refuse cannot be written at a call site.
 */
type QueryValues = Readonly<Record<string, string | number | boolean | undefined>>;

type ParamsOption<TRoute extends AnyRoute> = [PathParams<TRoute['path']>] extends [never]
  ? { readonly params?: never }
  : { readonly params: Readonly<Record<PathParams<TRoute['path']>, string>> };

/**
 * What a route's request carries: a typed JSON body, a `FormData`, or nothing.
 *
 * The three are mutually exclusive by construction rather than by a check inside the client: a
 * multipart route demands a `form` and refuses a `body`, a JSON route the reverse, and a route with
 * neither accepts neither. The failure this prevents is the quiet one — a photograph upload sent as
 * `JSON.stringify(formData)`, which serialises to `{}` and is refused by the server's multipart
 * reader with a message about a missing part.
 */
type BodyOption<TRoute extends AnyRoute> = TRoute['multipart'] extends true
  ? { readonly form: FormData; readonly body?: never }
  : [BodyOf<TRoute>] extends [never]
    ? { readonly body?: never; readonly form?: never }
    : { readonly body: BodyOf<TRoute>; readonly form?: never };

export type RequestOptions<TRoute extends AnyRoute> = ParamsOption<TRoute> &
  BodyOption<TRoute> & {
    /**
     * The query string, named by the route's own contract schema.
     *
     * A route that declares none resolves this to `never`, so it cannot be given one at all — which
     * is the half that was missing when every route's query was an open `Record<string, …>` and the
     * analytics dashboard sent a `granularity` five panels refuse.
     */
    readonly query?: QueryOf<TRoute>;
    /**
     * The key that makes a retry of this submission a replay rather than a second write. Required
     * on the four routes whose contract declares it; optional elsewhere, where the client mints one
     * per call so a transport-level retry is still covered.
     */
    readonly idempotencyKey?: string;
    readonly signal?: AbortSignal;
  };

export interface ApiClientOptions {
  /** The platform's `fetch`, or a test's. Never captured from the global at module scope. */
  readonly fetchImpl: typeof globalThis.fetch;
  /** The operator's language, read per request so a switch takes effect immediately. */
  readonly locale: () => Locale;
  /** The double-submit token the session is currently holding, or `null` before there is one. */
  readonly csrfToken: () => string | null;
  /**
   * Called with every refusal before it is thrown.
   *
   * This is how an idle lock and an expired session reach the shell without every call site
   * checking for them: the session provider subscribes, sees `AUTH_SESSION_IDLE_LOCKED`, and raises
   * the re-authentication overlay OVER whatever the operator was doing. The refusal is still
   * thrown, so the caller's own error handling runs too.
   */
  readonly onFailure?: (error: ApiError) => void;
}

/** A file this client fetched, with the name the server said to save it under. */
interface DownloadedFile {
  readonly blob: Blob;
  /** From `Content-Disposition`, or `null` when the server named none. */
  readonly filename: string | null;
}

export interface ApiClient {
  request: <TRoute extends AnyRoute>(
    route: TRoute,
    options: RequestOptions<TRoute>,
  ) => Promise<ResponseOf<TRoute>>;
  /**
   * A byte-answering route, fetched through this client rather than by the browser.
   *
   * **Two of the three downloads in this product cannot be a plain link**, and the reason is a
   * good one: an export artifact and a batch artifact are sealed under a one-time password, and the
   * routes take it in the `X-Export-Password` HEADER rather than in a query parameter — because a
   * query parameter would put the secret that opens a list of live pass codes into two access logs
   * and a browser history. A header cannot be set on a navigation, so those two are fetched here
   * and handed to the browser as a blob.
   *
   * The archive download is NOT one of them: it carries no password and is a plain link, which is
   * what streams a multi-gigabyte file instead of buffering it into memory.
   */
  download: <TPath extends string>(
    route: BinaryRouteSpec<TPath>,
    options: {
      readonly params: Readonly<Record<PathParams<TPath>, string>>;
      readonly headers?: Readonly<Record<string, string>>;
    },
  ) => Promise<DownloadedFile>;
  /**
   * A response that is designed never to finish, read frame by frame.
   *
   * **The third shape of call this application makes**, beside a JSON request and a byte download,
   * and it is here rather than in a hook for the reasons the other two are: it needs the session's
   * language, it needs `credentials: 'same-origin'` so the cookie travels, and above all it needs
   * the `onFailure` hook — a stream refused because the session expired has to raise the overlay
   * exactly as every other refusal does, or a terminal sits looking healthy with a dead connection.
   *
   * It resolves when the stream ends, and rejects with the same {@link ApiError} or
   * {@link TransportError} every other call rejects with, so a caller decides what to do about a
   * status rather than guessing from a reconnect that never succeeds.
   */
  stream: <TPath extends string>(
    route: BinaryRouteSpec<TPath>,
    options: {
      readonly signal: AbortSignal;
      readonly onFrame: (frame: SseFrame) => void;
    },
  ) => Promise<void>;
}

/** The file name a `Content-Disposition` names, or `null`. Quoted or bare, both real in the wild. */
function filenameFrom(disposition: string | null): string | null {
  if (disposition === null) {
    return null;
  }
  const quoted = /filename="([^"]+)"/u.exec(disposition)?.[1];
  const bare = /filename=([^;]+)/u.exec(disposition)?.[1];
  return quoted ?? bare?.trim() ?? null;
}

/** The header the SPA echoes the double-submit token in, matching `auth/csrf.ts`. */
export const CSRF_HEADER = 'X-CSRF-Token';

/** The header a client mints its idempotency key in, matching `http/idempotency.ts`. */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/** The header a masked fault is quotable by, matching `http/errors.ts`. */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/**
 * A route's path with its parameters substituted, for a caller that is not making a request.
 *
 * Its callers are the full-size photograph's `<img src>` and the archive download link: those bytes
 * are fetched by the browser rather than by this client, and the alternative to exporting this is a
 * path literal typed into a component, outside the drift test that checks every path in the route
 * table against the generated API document. A thumbnail is NOT one of them any more: every face is
 * drawn through `api/thumbnails.ts`, a page of them per request. The parameter names are still
 * checked by {@link PathParams}, so a renamed or misspelt one does not compile.
 */
export function pathOf<TPath extends string>(
  route: { readonly path: TPath },
  params: Readonly<Record<PathParams<TPath>, string>>,
): string {
  return fillPath(route.path, params);
}

function fillPath(path: string, params: Readonly<Record<string, string>> | undefined): string {
  if (params === undefined) {
    return path;
  }
  return path.replace(/:([A-Za-z]\w*)/gu, (_whole: string, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new TypeError(`${path} needs a value for :${name}`);
    }
    return encodeURIComponent(value);
  });
}

function appendQuery(path: string, query: QueryValues | undefined): string {
  if (query === undefined) {
    return path;
  }
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined) {
      search.set(name, String(value));
    }
  }
  const rendered = search.toString();
  return rendered.length === 0 ? path : `${path}?${rendered}`;
}

/**
 * How long a 429 that carried no `Retry-After` is taken to last, in seconds.
 *
 * Five, because that is what the outer proxy says with its own refusal (`deploy/00-newapp-http.conf`
 * maps every 429 it raises to `Retry-After: 5`), and the outer proxy is the only thing in front of
 * this application that answers a 429 outside the error contract.
 */
const PROXY_RETRY_AFTER_SECONDS = 5;

/**
 * What a non-2xx answer becomes, for every way this client makes a request.
 *
 * **A contract body wins**, because the server said exactly what it refused. **A 429 without one
 * is still a rate limit**, and saying so is the whole fix: the outer proxy refuses a burst with an
 * HTML page, which used to become a {@link TransportError} — and a transport failure is retried, so
 * every refusal by the proxy turned into up to three more requests against the very limit that had
 * just been reached. A refusal is never retried as though the network had dropped it. Anything
 * else off-contract is still a transport failure, because a 502 page really is the api being gone.
 */
function failureOf(routeId: string, response: Response, body: unknown): ApiError | TransportError {
  const meta = {
    status: response.status,
    retryAfterSeconds: retryAfterSecondsOf(response.headers),
    correlationId: response.headers.get(CORRELATION_HEADER),
  };
  const failure = apiErrorFrom(body, meta);
  if (failure !== null) {
    return failure;
  }
  if (response.status === 429) {
    return new ApiError({
      code: 'SYSTEM_RATE_LIMITED',
      status: 429,
      // Nothing the browser renders reads this: the message comes from the code, and the one
      // placeholder it asks for is filled from `retryAfterSeconds`, which is always set here.
      serverMessage: '',
      retryAfterSeconds: meta.retryAfterSeconds ?? PROXY_RETRY_AFTER_SECONDS,
      correlationId: meta.correlationId,
    });
  }
  return new TransportError(`${routeId} answered ${String(response.status)} off-contract`);
}

/** `Retry-After` in whole seconds, or `null` when the header is absent or not a number. */
function retryAfterSecondsOf(headers: Headers): number | null {
  const raw = headers.get('Retry-After');
  if (raw === null) {
    return null;
  }
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

/**
 * The serialised request body, or `undefined` for a route that takes none.
 *
 * The key goes into the body as well as the header on the four routes whose contract declares it,
 * because the schema requires it and a caller should not have to mint the same uuid twice. The
 * header still wins at the server, which is what makes a transport-level retry the thing this
 * guards.
 */
function jsonPayloadFor(
  route: AnyRoute,
  body: unknown,
  idempotencyKey: string | undefined,
): string | undefined {
  if (route.body === null) {
    return undefined;
  }
  const carried =
    route.idempotencyInBody && idempotencyKey !== undefined
      ? { ...(body as object), idempotencyKey }
      : body;
  return JSON.stringify(carried);
}

/**
 * The headers one request carries, beyond its content type.
 *
 * The double-submit token and the idempotency key go on unsafe methods only: there is nothing to
 * replay on a read and nothing to forge either, and sending them anyway would put a key on requests
 * the server's middleware would then have to be taught to ignore.
 */
function headersFor(
  route: AnyRoute,
  options: ApiClientOptions,
  idempotencyKey: string | undefined,
): Headers {
  const headers = new Headers({
    Accept: 'application/json',
    'Accept-Language': options.locale(),
  });

  if (!UNSAFE_METHODS.has(route.method)) {
    return headers;
  }

  // Named `doubleSubmit` rather than `token`: the static-analysis rule that hunts for a timing
  // attack keys on the NAME, and this is a header being set rather than a secret being compared.
  const doubleSubmit = options.csrfToken();
  if (doubleSubmit !== null) {
    headers.set(CSRF_HEADER, doubleSubmit);
  }
  if (idempotencyKey !== undefined) {
    headers.set(IDEMPOTENCY_HEADER, idempotencyKey);
  }
  return headers;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  async function request<TRoute extends AnyRoute>(
    route: TRoute,
    call: RequestOptions<TRoute>,
  ): Promise<ResponseOf<TRoute>> {
    const unsafe = UNSAFE_METHODS.has(route.method);
    const idempotencyKey = call.idempotencyKey ?? (unsafe ? newIdempotencyKey() : undefined);

    const headers = headersFor(route, options, idempotencyKey);

    // A multipart body is handed over untouched and its `Content-Type` is deliberately NOT set: the
    // platform writes it, with the boundary it generated, and a header set here would name a
    // boundary that is not in the body.
    const payload: BodyInit | undefined = route.multipart
      ? call.form
      : jsonPayloadFor(route, call.body, idempotencyKey);
    if (payload !== undefined && !route.multipart) {
      headers.set('Content-Type', 'application/json');
    }

    // No cast: `QueryOf<TRoute>` maps every member to what a query string can hold, so it satisfies
    // `appendQuery` on its own. The key set was already decided at the call site by the route's own
    // schema; what happens here is only serialisation.
    const url = appendQuery(fillPath(route.path, call.params), call.query);

    let response: Response;
    try {
      response = await options.fetchImpl(url, {
        method: route.method,
        headers,
        credentials: 'same-origin',
        // `no-store` on the request as well as on every answer: a decrypted record must never sit
        // in a disk cache, and a browser is entitled to cache a GET that says nothing about it.
        cache: 'no-store',
        ...(payload === undefined ? {} : { body: payload }),
        ...(call.signal === undefined ? {} : { signal: call.signal }),
      });
    } catch (error) {
      throw new TransportError(`${route.id} could not be reached`, { cause: error });
    }

    const body = await readBody(response);

    if (!response.ok) {
      const failure = failureOf(route.id, response, body);
      if (failure instanceof ApiError) {
        options.onFailure?.(failure);
      }
      throw failure;
    }

    const parsed = route.response.safeParse(body);
    if (!parsed.success) {
      throw new ResponseShapeError(route.id);
    }
    return parsed.data as ResponseOf<TRoute>;
  }

  async function download<TPath extends string>(
    route: BinaryRouteSpec<TPath>,
    call: {
      readonly params: Readonly<Record<PathParams<TPath>, string>>;
      readonly headers?: Readonly<Record<string, string>>;
    },
  ): Promise<DownloadedFile> {
    const headers = new Headers({ 'Accept-Language': options.locale() });
    for (const [name, value] of Object.entries(call.headers ?? {})) {
      headers.set(name, value);
    }

    let response: Response;
    try {
      response = await options.fetchImpl(fillPath(route.path, call.params), {
        method: route.method,
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
      });
    } catch (error) {
      throw new TransportError(`${route.id} could not be reached`, { cause: error });
    }

    if (!response.ok) {
      // The refusal body is JSON even though the success is bytes, so it is read the same way every
      // other refusal in this client is — and an off-contract failure is a transport failure rather
      // than a blob somebody tries to save.
      const failure = failureOf(route.id, response, await readBody(response));
      if (failure instanceof ApiError) {
        options.onFailure?.(failure);
      }
      throw failure;
    }

    return {
      blob: await response.blob(),
      filename: filenameFrom(response.headers.get('Content-Disposition')),
    };
  }

  async function stream<TPath extends string>(
    route: BinaryRouteSpec<TPath>,
    call: { readonly signal: AbortSignal; readonly onFrame: (frame: SseFrame) => void },
  ): Promise<void> {
    let response: Response;
    try {
      response = await options.fetchImpl(route.path, {
        method: route.method,
        headers: new Headers({
          Accept: 'text/event-stream',
          'Accept-Language': options.locale(),
        }),
        credentials: 'same-origin',
        cache: 'no-store',
        signal: call.signal,
      });
    } catch (error) {
      // **An abort is not a failure, and the order of these two checks is the whole of it.** The
      // subscription aborts on every unmount, on every sign-out and twice on mount under
      // `<StrictMode>`, and `fetch` reports all of them by rejecting. Reading them as transport
      // failures would advance the backoff and log a fault on a connection that was closed on
      // purpose.
      if (call.signal.aborted) {
        return;
      }
      throw new TransportError(`${route.id} could not be reached`, { cause: error });
    }

    if (!response.ok) {
      const failure = failureOf(route.id, response, await readBody(response));
      if (failure instanceof ApiError) {
        options.onFailure?.(failure);
      }
      throw failure;
    }

    const body = response.body;
    if (body === null) {
      throw new TransportError(`${route.id} answered with no body`);
    }

    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          return;
        }
        for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
          call.onFrame(frame);
        }
      }
    } catch (error) {
      if (call.signal.aborted) {
        return;
      }
      throw new TransportError(`${route.id} stopped early`, { cause: error });
    } finally {
      reader.releaseLock();
    }
  }

  return { request, download, stream };
}
